import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AlertComponent } from '@components';
import { Admin, Role, ServiceProvider } from '@models';
import { UtilService } from '@services';
import {
  addServiceProviderAdmin,
  addServiceProviderAdminFailed,
  addServiceProviderAdminSuccess,
} from '@state';

@Component({
  selector: 'app-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatInputModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    AlertComponent,
  ],
  templateUrl: './admin.dialog.html',
  styleUrl: './admin.dialog.scss',
})
export class AdminDialog {
  private readonly dialogRef = inject(MatDialogRef<AdminDialog>);
  public readonly data = inject(MAT_DIALOG_DATA);

  public form = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required]),
  });

  public serviceProvider: ServiceProvider = new ServiceProvider();
  public error: string | null = null;
  public loading: boolean = false;

  constructor(private utils: UtilService) {
    this.serviceProvider = this.data.serviceProvider;
  }

  public async onFormSubmit(): Promise<void> {
    if (!this.form.valid) {
      this.error = 'Please fill in all required fields';
      return;
    }

    this.loading = true;
    try {
      const admin: Admin = {
        email: this.getFormControl('email').value,
        password: this.getFormControl('password').value,
        role: Role.SERVICE_PROVIDER_ADMIN,
        serviceProviderId: this.serviceProvider.id!,
      };

      await this.utils.actionsToPromise(
        addServiceProviderAdmin({ serviceProvider: this.serviceProvider, admin }),
        addServiceProviderAdminSuccess,
        addServiceProviderAdminFailed
      );

      this.dialogRef.close();
    } catch (error) {
      this.error = this.utils.getErrorMessage(error);
    }
    this.loading = false;
  }

  public getFormControl(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
